import React, { useState, useEffect } from 'react';
import Layout, { PageKey } from '../components/Layout';
import { getSettings, saveSettings, getAdAccounts, getWpStatus, testAllWp, importAllWp } from '../api';

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  onNavigate: (page: PageKey) => void;
}

interface SettingsData {
  HIGGSFIELD_API_KEY: string;
  HIGGSFIELD_SET: boolean;
  AWS_S3_BUCKET: string;
  AWS_ACCESS_KEY: string;
  AWS_SECRET_KEY: string;
  AWS_SET: boolean;
}

export default function Settings({ user, onLogout, onNavigate }: Props) {
  const [data, setData] = useState<SettingsData | null>(null);
  const [higgsfieldKey, setHiggsfieldKey] = useState('');
  const [awsBucket, setAwsBucket] = useState('');
  const [awsAccess, setAwsAccess] = useState('');
  const [awsSecret, setAwsSecret] = useState('');
  const [saving, setSaving] = useState<'higgsfield' | 'aws' | null>(null);
  const [saved, setSaved] = useState<'higgsfield' | 'aws' | null>(null);
  const [error, setError] = useState('');

  // WordPress overview
  const [wpAccounts, setWpAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [wpStatus, setWpStatus] = useState<Record<string, any>>({});
  const [wpBusy, setWpBusy] = useState('');
  const [wpMsg, setWpMsg] = useState('');

  const loadWpStatus = () => getWpStatus().then(r => setWpStatus(r.data || {})).catch(() => {});
  useEffect(() => {
    getAdAccounts()
      .then(r => setWpAccounts((r.data.accounts || []).filter((a: any) => a.active)
        .map((a: any) => ({ id: a.id, name: a.business_name?.trim() || a.page_name?.trim() || a.name || a.id }))))
      .catch(() => {});
    loadWpStatus();
  }, []);

  const handleTestAll = async () => {
    setWpBusy('test'); setWpMsg('');
    try { await testAllWp(); await loadWpStatus(); setWpMsg('✓ Alle Verbindungen getestet'); }
    catch { setWpMsg('Fehler beim Testen'); } finally { setWpBusy(''); }
  };
  const handleImportAll = async () => {
    setWpBusy('import'); setWpMsg('');
    try { const r = await importAllWp();
      const total = Object.values(r.data || {}).reduce((s: number, x: any) => s + (x?.imported || 0), 0);
      await loadWpStatus(); setWpMsg(`✓ ${total} Einträge importiert`);
    } catch { setWpMsg('Fehler beim Import'); } finally { setWpBusy(''); }
  };

  const wpState = (id: string): 'connected' | 'error' | 'none' => {
    const e = wpStatus[id];
    if (!e || !e.has_config) return 'none';
    return e.wp_status === 'connected' ? 'connected' : 'error';
  };
  const wpCounts = wpAccounts.reduce((a, acc) => {
    a[wpState(acc.id)]++; return a;
  }, { connected: 0, error: 0, none: 0 } as Record<string, number>);

  useEffect(() => {
    getSettings()
      .then(res => {
        setData(res.data);
        setAwsBucket(res.data.AWS_S3_BUCKET || '');
        setAwsAccess(res.data.AWS_ACCESS_KEY || '');
      })
      .catch(() => {});
  }, []);

  const handleSaveHighgsfield = async () => {
    if (!higgsfieldKey.trim()) return;
    setSaving('higgsfield');
    setError('');
    try {
      await saveSettings({ HIGGSFIELD_API_KEY: higgsfieldKey.trim() });
      setData(d => d ? { ...d, HIGGSFIELD_SET: true } : d);
      setHiggsfieldKey('');
      setSaved('higgsfield');
      setTimeout(() => setSaved(null), 2500);
    } catch {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveAws = async () => {
    setSaving('aws');
    setError('');
    try {
      const payload: Record<string, string> = {
        AWS_S3_BUCKET: awsBucket.trim(),
        AWS_ACCESS_KEY: awsAccess.trim(),
      };
      if (awsSecret.trim()) payload.AWS_SECRET_KEY = awsSecret.trim();
      await saveSettings(payload);
      setData(d => d ? { ...d, AWS_SET: !!awsBucket.trim() } : d);
      setAwsSecret('');
      setSaved('aws');
      setTimeout(() => setSaved(null), 2500);
    } catch {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="settings" onNavigate={onNavigate}>
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={s.title}>Einstellungen</h1>
          <p style={s.subtitle}>API-Keys und Cloud-Speicher konfigurieren</p>
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        {/* Integrationen */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <SparkIcon />
            <span style={s.sectionTitle}>Integrationen</span>
          </div>

          <div style={s.card}>
            <div style={s.cardRow}>
              <div style={s.cardLeft}>
                <div style={s.fieldLabel}>Higgsfield API Key</div>
                <div style={s.fieldHint}>
                  Für die KI-gestützte Creative-Generierung in PS-Brain.{' '}
                  {data?.HIGGSFIELD_SET && <span style={s.badge}>✓ Konfiguriert</span>}
                </div>
              </div>
              <div style={s.cardRight}>
                <input
                  type="password"
                  placeholder={data?.HIGGSFIELD_SET ? '••••••••••••' : 'hf_xxxxxxxxxxxxxxxx'}
                  value={higgsfieldKey}
                  onChange={e => setHiggsfieldKey(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveHighgsfield()}
                  style={s.input}
                  autoComplete="new-password"
                />
                <button
                  onClick={handleSaveHighgsfield}
                  disabled={saving === 'higgsfield' || !higgsfieldKey.trim()}
                  style={saved === 'higgsfield' ? s.btnSaved : s.btn}
                >
                  {saving === 'higgsfield' ? 'Speichern…' : saved === 'higgsfield' ? '✓ Gespeichert' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Cloud-Speicher */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <CloudIcon />
            <span style={s.sectionTitle}>Cloud-Speicher (AWS S3)</span>
            <span style={s.optionalBadge}>Optional</span>
          </div>
          <div style={s.sectionHint}>
            Ohne S3-Konfiguration werden Creatives lokal im Server-Verzeichnis gespeichert.
            Mit S3 werden alle Assets automatisch in der Cloud abgelegt.
          </div>

          <div style={s.card}>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>S3 Bucket Name</label>
              <input
                type="text"
                placeholder="mein-bucket-name"
                value={awsBucket}
                onChange={e => setAwsBucket(e.target.value)}
                style={s.input}
              />
            </div>
            <div style={s.fieldRow}>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel}>AWS Access Key ID</label>
                <input
                  type="text"
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  value={awsAccess}
                  onChange={e => setAwsAccess(e.target.value)}
                  style={s.input}
                />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel}>AWS Secret Access Key</label>
                <input
                  type="password"
                  placeholder={data?.AWS_SET ? '••••••••••••' : 'wJalrXUtnFEMI/K7MDENG/…'}
                  value={awsSecret}
                  onChange={e => setAwsSecret(e.target.value)}
                  style={s.input}
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div style={s.cardActions}>
              {data?.AWS_SET && <span style={s.badge}>✓ S3 konfiguriert</span>}
              <button
                onClick={handleSaveAws}
                disabled={saving === 'aws'}
                style={saved === 'aws' ? s.btnSaved : s.btn}
              >
                {saving === 'aws' ? 'Speichern…' : saved === 'aws' ? '✓ Gespeichert' : 'S3-Einstellungen speichern'}
              </button>
            </div>
          </div>
        </div>

        {/* WordPress-Verbindungen */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <WpIcon />
            <span style={s.sectionTitle}>WordPress-Verbindungen</span>
            <span style={s.optionalBadge}>
              {wpCounts.connected} verbunden · {wpCounts.error} fehlerhaft · {wpCounts.none} ohne
            </span>
          </div>
          <div style={s.sectionHint}>
            Übersicht aller Partner mit ihrem WordPress-Verbindungsstatus für echte Lead-Zahlen (FluentForms).
            Einrichtung & App-Passwort pro Partner im jeweiligen Partner-Profil.
          </div>

          <div style={s.card}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' as const, marginBottom: 4 }}>
              <button onClick={handleTestAll} disabled={wpBusy === 'test'} style={s.btn}>
                {wpBusy === 'test' ? 'Teste alle…' : 'Alle testen'}
              </button>
              <button onClick={handleImportAll} disabled={wpBusy === 'import'} style={s.btn}>
                {wpBusy === 'import' ? 'Importiere alle…' : 'Alle importieren'}
              </button>
              {wpMsg && <span style={{ fontSize: 12.5, color: wpMsg.startsWith('✓') ? '#059669' : '#cc2a22' }}>{wpMsg}</span>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {wpAccounts.length === 0 && <div style={s.fieldHint}>Keine Partner geladen.</div>}
              {wpAccounts.map(acc => {
                const st = wpState(acc.id);
                const color = { connected: '#34c759', error: '#ff9500', none: '#ff3b30' }[st];
                const label = { connected: 'Verbunden', error: 'Fehlerhaft', none: 'Keine Verbindung' }[st];
                const e = wpStatus[acc.id] || {};
                return (
                  <div key={acc.id} style={s.wpRow}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 13, color: '#1d1d1f', fontWeight: 500 }}>{acc.name}</span>
                    {e.lead_count > 0 && <span style={s.wpLeadCount}>{e.lead_count} Leads</span>}
                    <span style={{ fontSize: 12, color, fontWeight: 600 }}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function WpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#1a3a5c">
      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-8.5 10c0-1.232.264-2.402.736-3.46l4.052 11.1A8.503 8.503 0 0 1 3.5 12zm8.5 8.5c-.834 0-1.64-.122-2.401-.345l2.551-7.413 2.613 7.159c.017.042.038.08.06.117A8.464 8.464 0 0 1 12 20.5zm1.171-12.49c.512-.027.973-.081.973-.081.458-.054.404-.727-.054-.7 0 0-1.377.108-2.266.108-.835 0-2.24-.108-2.24-.108-.458-.027-.512.673-.054.7 0 0 .424.054.884.081l1.327 3.637-1.864 5.587-3.101-9.224c.512-.027.973-.081.973-.081.458-.054.403-.727-.055-.7 0 0-1.376.108-2.265.108-.16 0-.347-.004-.547-.01A8.49 8.49 0 0 1 12 3.5c2.213 0 4.228.846 5.74 2.232-.037-.002-.072-.007-.11-.007-.835 0-1.427.727-1.427 1.508 0 .701.404 1.293.835 1.994.324.566.701 1.293.701 2.344 0 .727-.28 1.572-.646 2.748l-.848 2.833-3.074-9.142z"/>
    </svg>
  );
}

function SparkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a3a5c"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
}
function CloudIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a3a5c"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
    </svg>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { padding: '40px 48px', maxWidth: 860, margin: '0 auto' },
  header: { marginBottom: 40 },
  title: { fontSize: 28, fontWeight: 800, color: '#1d1d1f', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { margin: '6px 0 0', color: '#6e6e73', fontSize: 14 },
  errorBox: {
    background: '#fff2f2', border: '1px solid rgba(255,59,48,.25)', borderRadius: 10,
    padding: '12px 16px', color: '#cc2a22', fontSize: 13, marginBottom: 24,
  },
  section: { marginBottom: 40 },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 8, color: '#1d1d1f',
  },
  sectionTitle: { fontSize: 17, fontWeight: 700 },
  sectionHint: { fontSize: 12.5, color: '#6e6e73', marginBottom: 16, lineHeight: 1.5 },
  optionalBadge: {
    fontSize: 10.5, fontWeight: 600, color: '#6e6e73',
    background: '#f0f0f5', borderRadius: 5, padding: '2px 7px', letterSpacing: '0.04em',
  },
  card: {
    background: '#fff', borderRadius: 14, border: '1px solid #e8e8ed',
    padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16,
  },
  cardRow: { display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' as const },
  cardLeft: { flex: 1, minWidth: 200 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 10, flex: 1.5, minWidth: 260, flexWrap: 'wrap' as const },
  cardActions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, paddingTop: 4 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 220 },
  fieldRow: { display: 'flex', gap: 16, flexWrap: 'wrap' as const },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: '#1d1d1f' },
  fieldHint: { fontSize: 12, color: '#6e6e73', marginTop: 3 },
  input: {
    flex: 1, minWidth: 0,
    border: '1.5px solid #e8e8ed', borderRadius: 8, padding: '9px 12px',
    fontSize: 13, color: '#1d1d1f', outline: 'none', background: '#fafafa',
    fontFamily: 'monospace',
  },
  btn: {
    background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8,
    padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap' as const, flexShrink: 0, transition: 'background .15s',
  },
  btnSaved: {
    background: '#10b981', color: '#fff', border: 'none', borderRadius: 8,
    padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'default',
    whiteSpace: 'nowrap' as const, flexShrink: 0,
  },
  badge: {
    fontSize: 11.5, fontWeight: 600, color: '#059669',
    background: '#d1fae5', borderRadius: 6, padding: '3px 8px',
  },
  wpRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 0', borderBottom: '1px solid #f0f0f5',
  },
  wpLeadCount: {
    fontSize: 11, fontWeight: 600, color: '#1a3a5c',
    background: 'rgba(26,58,92,0.08)', borderRadius: 5, padding: '2px 7px',
  },
};
